/*
 * coldest-cro-framework-pdp.js
 * -------------------------------------------------------------------------
 * Scoped to the CRO framework PDP. Loaded via a custom_liquid block in
 * templates/product.cro-framework.json, so it ONLY runs on products that use
 * the `cro-framework` template suffix (the drinkware set we deployed against).
 *
 * Fixes two PDP behaviours:
 *
 *  1) ADD TO CART -> AOV.ai (Avada) sliding cart drawer, not the /cart page.
 *     The store's cart_type is "page", so the native product form did a
 *     full-page POST to /cart. (cldst-atc-fast.js intercepts collection-card
 *     forms but EXPLICITLY EXCLUDES the PDP form, so the PDP add was never
 *     AJAXified.) We intercept the PDP submit, add via /cart/add.js, then open
 *     the drawer via the canonical window.coldestOpenCartDrawer()
 *     (snippets/coldest-atc-drawer.liquid); the old `theme:cartchanged` +
 *     header .cart-link click remains only as a fallback. We never
 *     fall back to form.submit() — that full-page nav to /cart is exactly the
 *     friction we're removing (see cldst-atc-fast.js notes). On error we retry
 *     once, then show a non-blocking toast.
 *     Buy-It-Now is unaffected: it uses form.submit(), which fires no submit
 *     event. The loading spinner is the store's existing `.cldst-atc-loading`
 *     (toggled by cldst-atc-fast on click, cleared on `theme:cartchanged`), so
 *     we never touch the button's rich label markup.
 *
 *  2) VARIANT CHANGE -> swap the main gallery image. The main gallery is
 *     rendered by the external PMSlider app (.pmslider-slide img), which does
 *     not react to variant changes, so we swap it ourselves from the selected
 *     variant's featured image (read from window.pmsliderProductPreload).
 * -------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var PRODUCT_FORM = "form.product-purchase-form";

  /* PMSlider preloads the full product JSON; reuse it as our source of truth. */
  function getProduct() {
    var p = window.pmsliderProductPreload;
    return p && p.variants ? p : null;
  }

  /* ====================================================================== */
  /* 1) ADD TO CART -> AOV.ai cart drawer                                    */
  /* ====================================================================== */

  function toast(msg) {
    try {
      var t = document.createElement("div");
      t.textContent = msg;
      t.setAttribute("role", "alert");
      t.style.cssText =
        "position:fixed;top:20px;left:50%;transform:translateX(-50%);" +
        "background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:6px;" +
        "z-index:99999;font-size:14px;font-weight:500;" +
        "box-shadow:0 4px 14px rgba(0,0,0,0.25);max-width:90vw";
      document.body.appendChild(t);
      setTimeout(function () { t.remove(); }, 4000);
    } catch (e) {}
  }

  function updateCartCount(cart) {
    if (!cart) return;
    document
      .querySelectorAll(".cart-count, [data-cart-count], .cart-link__count, .cart-link__bubble, .cart-item-count, .header-cart-count")
      .forEach(function (el) { el.textContent = cart.item_count; });
  }

  /* Clear the store's ATC loading spinner directly (used on failure paths,
     where firing a cart-changed event would be misleading and could throw in
     other listeners that assume detail.cart exists). */
  function clearLoadingSpinner() {
    document.querySelectorAll(".cldst-atc-loading").forEach(function (el) {
      el.classList.remove("cldst-atc-loading");
    });
  }

  function dispatchCartEvents(cart) {
    /* theme:cartchanged is the store's canonical cart-change event (it also
       clears the .cldst-atc-loading spinner). Fire the rest for any other
       listener / the AOV.ai drawer. */
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:cartchanged", { bubbles: true, cancelable: false, detail: { cart: cart } })
      );
    } catch (e) {}
    ["cart:updated", "cart:refresh", "cart:open", "ajaxProduct:added"].forEach(function (ev) {
      try { document.dispatchEvent(new CustomEvent(ev, { detail: { cart: cart }, bubbles: true })); } catch (e) {}
      try { window.dispatchEvent(new CustomEvent(ev, { detail: { cart: cart }, bubbles: true })); } catch (e) {}
    });
  }

  /* Is a cart drawer already visible? (display:none elements — e.g. the
     theme's disabled native drawer — have a null offsetParent, so they don't
     count.) Lets us avoid toggling the AOV.ai drawer shut if it auto-opened. */
  function isCartDrawerOpen() {
    var sels = [
      ".avada-cart-drawer", "#ava-cart-drawer", ".ava-cart-drawer", ".ava-cart",
      '[class*="avada"][class*="cart"]',
      ".cart-drawer.is-open", ".cart-drawer.active", ".cart-drawer--open",
      ".drawer--cart.is-open", '[class*="cart"][class*="drawer"]'
    ];
    for (var i = 0; i < sels.length; i++) {
      var els;
      try { els = document.querySelectorAll(sels[i]); } catch (e) { continue; }
      for (var j = 0; j < els.length; j++) {
        var el = els[j];
        if (el.offsetParent !== null && el.getBoundingClientRect().width > 50) return true;
      }
    }
    if (/cart-(open|drawer-open)|drawer-open|ava-cart-open|avada-open/.test(document.body.className)) return true;
    return false;
  }

  function clickCartLink() {
    /* If the drawer didn't auto-open from the add, click the cart link the
       AOV.ai drawer binds to. The delay lets an auto-open animation register
       so we don't click it shut. */
    setTimeout(function () {
      if (isCartDrawerOpen()) return;
      var link = document.querySelector('.cart-link, a[href$="/cart"], a[href="/cart"]');
      if (link) { try { link.click(); } catch (e) {} }
    }, 400);
  }

  function openCartDrawer(cart) {
    /* Canonical opener (snippets/coldest-atc-drawer.liquid, rendered in <head>):
       guarded cart-icon click + cart events + bubble refresh. The local
       events + cart-link click below are only a fallback if it is missing. */
    if (typeof window.coldestOpenCartDrawer === "function") {
      try { window.coldestOpenCartDrawer(); return; } catch (e) {}
    }
    if (cart) dispatchCartEvents(cart);
    clickCartLink();
  }

  function doAdd(form, attempt) {
    attempt = attempt || 1;
    fetch("/cart/add.js", {
      method: "POST",
      body: new FormData(form),
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin"
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; }, function () { throw new Error("add_failed_" + r.status); });
        return r.json();
      })
      .then(function () {
        return fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateCartCount(cart);
            openCartDrawer(cart);
            form.dataset.croBusy = "";
          })
          .catch(function () {
            /* Add succeeded but the cart read failed — still open the drawer. */
            clearLoadingSpinner();
            openCartDrawer(null);
            form.dataset.croBusy = "";
          });
      })
      .catch(function (err) {
        var desc = err && (err.description || err.message);
        if (/Engraving text required/.test(String(desc))) {
          /* The engraving guard (coldest-phase2-fixes.js) blocked the add and
             already showed the field error — not transient, don't retry/toast. */
          form.dataset.croBusy = "";
          clearLoadingSpinner();
          return;
        }
        if (/sold|stock|unavailable|422/i.test(String(desc))) {
          /* A real inventory error — surface it, don't retry/navigate. */
          form.dataset.croBusy = "";
          clearLoadingSpinner();
          toast(err.description || "Sorry, that option isn't available right now.");
          return;
        }
        if (attempt < 2) {
          setTimeout(function () { doAdd(form, attempt + 1); }, 350);
          return;
        }
        form.dataset.croBusy = "";
        clearLoadingSpinner();
        toast("Couldn't add to cart. Please try again.");
        try { console.warn("[cro-pdp] ajax add failed", err); } catch (e) {}
      });
  }

  function handleAtcSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== "FORM" || !form.matches(PRODUCT_FORM)) return;
    if (e.defaultPrevented) return;                 /* a validator/app already handled it */
    if (form.dataset.croBusy === "1") { e.preventDefault(); return; }
    /* Respect native HTML validation (e.g. required variant radios). */
    if (typeof form.checkValidity === "function" && !form.checkValidity()) return;

    e.preventDefault();
    form.dataset.croBusy = "1";
    doAdd(form, 1);
  }

  document.addEventListener("submit", handleAtcSubmit, false);

  /* ====================================================================== */
  /* 2) VARIANT CHANGE -> swap the main PMSlider image                       */
  /* ====================================================================== */

  function variantFeaturedSrc(variantId) {
    var p = getProduct();
    if (!p || !variantId) return null;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (String(v.id) === String(variantId)) {
        if (v.featured_image && v.featured_image.src) return v.featured_image.src;
        if (v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) {
          return v.featured_media.preview_image.src;
        }
        return null;
      }
    }
    return null;
  }

  function withWidth(src, w) {
    if (!src) return src;
    var clean = src.replace(/([?&])width=\d+/, "$1").replace(/[?&]$/, "");
    return clean + (clean.indexOf("?") > -1 ? "&" : "?") + "width=" + w;
  }

  function buildSrcset(src) {
    return [400, 600, 800, 1000, 1200, 1500]
      .map(function (w) { return withWidth(src, w) + " " + w + "w"; })
      .join(", ");
  }

  function selectedVariantId() {
    /* The form's id field is the source of truth (it's what gets added to
       cart and the theme keeps it in sync). URL ?variant= is only a fallback. */
    var input = document.querySelector(PRODUCT_FORM + ' [name="id"], form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return input.value;
    try {
      var u = new URLSearchParams(window.location.search).get("variant");
      if (u) return u;
    } catch (e) {}
    return null;
  }

  function swapMainImage() {
    var src = variantFeaturedSrc(selectedVariantId());
    if (!src) return;
    var srcset = buildSrcset(src);
    var full = withWidth(src, 1200);

    var imgs = [];
    var active = document.querySelector(
      '.pmslider-slide.is-active img, .pmslider-slide.is-selected img, .pmslider-slide[aria-hidden="false"] img'
    );
    if (active) imgs.push(active);
    var first = document.querySelector(".pmslider-slide img");
    if (first && imgs.indexOf(first) === -1) imgs.push(first);
    if (imgs.length === 0) {
      var fb = document.querySelector(
        ".main-image .slide--custom-initial img, .main-image .slick-current img, .gallery .main-image img, .main-image img"
      );
      if (fb) imgs.push(fb);
    }
    imgs.forEach(function (img) {
      img.setAttribute("srcset", srcset);
      img.setAttribute("src", full);
      img.removeAttribute("data-src");
      img.removeAttribute("loading");
    });
  }

  var lastVid = null;
  function onVariantChange() {
    setTimeout(swapMainImage, 60);
    setTimeout(swapMainImage, 260);
  }

  document.addEventListener("on:variant:change", onVariantChange);
  document.addEventListener("change", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (
      t.closest(".option-selectors") ||
      t.closest(".option-selector") ||
      t.closest("variant-picker") ||
      t.name === "id"
    ) {
      onVariantChange();
    }
  });
  if (window.jQuery) {
    try { window.jQuery(document).on("variantImageSelected", onVariantChange); } catch (e) {}
  }

  /* Safety net: catch any variant-change path we didn't bind to. */
  setInterval(function () {
    var v = selectedVariantId();
    if (v && v !== lastVid) {
      lastVid = v;
      swapMainImage();
    }
  }, 500);

  lastVid = selectedVariantId();
})();
